/* eslint-disable prettier/prettier */
import React from "react";
import { createUuid, type UUID } from "../..//utils/uuid";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import { store } from "../..//store";
import { type IColumn, type IObjectWithKey, SelectionMode, Selection } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import type { SelectionState } from "../..//utils/selection-state";
import type { PaginationState } from "../..//utils/pagination-state";
import type { SortState } from "../..//utils/sort-state";
import type {
    EloshieldClassicStationaryModuleSortKey,
    EloshieldClassicStationaryModule,
} from "../..//api";
import type {
    EloshieldClassicStationaryModulesApiResource,
    EloshieldClassicStationaryModulesQuery,
} from "../..//domain/repositories/repository-eloshield-classic-stationary-modules";
import { defaultPageSize } from "../..//utils/constants";
import { LayoutList } from "../layouts/layout-list";
import { ListEloshieldClassicStationaryModuleItemCell } from "../atoms/list-eloshield-classic-stationary-module-item-cell";
import { observer } from "mobx-react";

export interface ListEloshieldClassicStationaryModulesProps
    extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListEloshieldClassicStationaryModuleItem extends IObjectWithKey {
    readonly key: UUID;
}

@observer
export class ListEloshieldClassicStationaryModules extends React.Component<ListEloshieldClassicStationaryModulesProps> {
    private _listStateId = createUuid();
    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.eloshieldClassicStationaryModules.getSelectionState(
            this.listStateId,
        );
    }

    constructor(props: ListEloshieldClassicStationaryModulesProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.eloshieldClassicStationaryModules.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }
    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<EloshieldClassicStationaryModuleSortKey> {
        return store.serviceListStates.eloshieldClassicStationaryModules.getSortState(
            this.listStateId,
        );
    }

    @computed
    private get paginationState(): PaginationState<EloshieldClassicStationaryModulesApiResource> {
        return store.serviceListStates.eloshieldClassicStationaryModules.getPaginationState(
            this.listStateId,
        );
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [
            {
                fieldName: "label",
                name: store.i18n.t("listEloshieldClassicStationaryModules.column.label.name"),
                key: "label",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                fieldName: "serialNumber",
                name: store.i18n.t(
                    "listEloshieldClassicStationaryModules.column.serialNumber.name",
                ),
                key: "serialNumber",
                minWidth: 170,
                maxWidth: 170,
            },
            {
                fieldName: "Machine ID",
                name: store.i18n.t("listEloshieldClassicStationaryModules.column.machineId.name"),
                key: "machineId",
                minWidth: 100,
                maxWidth: 100,
            },
            {
                fieldName: "Antenna A network IDs",
                name: store.i18n.t(
                    "listEloshieldClassicStationaryModules.column.antennaANetworkIds.name",
                ),
                key: "antennaANetworkIds",
                minWidth: 150,
                maxWidth: 150,
            },
            {
                fieldName: "Antenna B network IDs",
                name: store.i18n.t(
                    "listEloshieldClassicStationaryModules.column.antennaBNetworkIds.name",
                ),
                key: "antennaBNetworkIds",
                minWidth: 150,
                maxWidth: 150,
            },
        ];
        return columns;
    }

    @computed private get eloshieldClassicStationaryModules(): (
        | EloshieldClassicStationaryModule
        | undefined
    )[] {
        if (this.props.ids) {
            return this.props.ids.map((id) =>
                store.repositoryEloshieldClassicStationaryModules.byId(id),
            );
        } else {
            return store.repositoryEloshieldClassicStationaryModules.byQuery(
                this.query,
                this.paginationState.pagination,
            );
        }
    }

    @computed private get items(): (ListEloshieldClassicStationaryModuleItem | undefined)[] {
        return this.eloshieldClassicStationaryModules.map((eloshieldClassicStationaryModule) => {
            if (!eloshieldClassicStationaryModule) {
                return;
            }
            return {
                key: eloshieldClassicStationaryModule.id,
            };
        });
    }

    @computed private get query(): EloshieldClassicStationaryModulesQuery {
        const query = {
            pageSize: defaultPageSize,
            ...this.sortState.query,
        } as EloshieldClassicStationaryModulesQuery;
        return query;
    }

    private renderItem(eloshieldClassicStationaryModuleId: UUID, column: IColumn): JSX.Element {
        return (
            <ListEloshieldClassicStationaryModuleItemCell
                eloshieldClassicStationaryModuleId={eloshieldClassicStationaryModuleId}
                column={column}
            />
        );
    }

    public render(): JSX.Element {
        if (
            !store.serviceListStates.eloshieldClassicStationaryModules.isInitialized(
                this.listStateId,
            )
        ) {
            return <></>;
        }

        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...this.props}
                    selection={this.selection}
                    selectionMode={SelectionMode.single}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    // Disable right-click to stay consistent with other lists
                    onItemContextMenu={() => {}}
                    onRenderItemColumn={(
                        item: ListEloshieldClassicStationaryModuleItem,
                        _index,
                        column,
                    ) => this.renderItem(item.key, column!)}
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
